import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  message,
  Breadcrumb,
  Divider,
  Row,
  Col,
  DatePicker,
  Select,
  Upload,
  Spin,
  Space,
} from "antd";
import {
  SaveOutlined,
  ArrowLeftOutlined,
  HomeOutlined,
  FileTextOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { articleAPIWithErrorHandling } from "../../../api/article/article";
import dayjs from "dayjs";

const { Option } = Select;

export default function UpdateArticle() {
  const { id } = useParams();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [currentThumbnail, setCurrentThumbnail] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const catRes = await articleAPIWithErrorHandling.getCategoryAll();
      setCategories(catRes.data || []);

      const artRes = await articleAPIWithErrorHandling.getArticleById(id);
      const article = artRes.data;

      form.setFieldsValue({
        ...article,
        publish_date: article.publish_date ? dayjs(article.publish_date) : null,
      });
      setCurrentThumbnail(article.thumbnail_url);
    } catch (error) {
      message.error(error.message || "Không thể tải dữ liệu bài viết");
      navigate("/articles");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("summary", values.summary || "");
      formData.append("content", values.content || "");
      formData.append("author", values.author || "");
      if (values.article_category_id) {
        formData.append("article_category_id", values.article_category_id);
      }
      if (values.publish_date) {
        formData.append("publish_date", values.publish_date.format("YYYY-MM-DD HH:mm:ss"));
      }
      formData.append("status", values.status ? 1 : 0);

      if (fileList.length > 0 && fileList[0].originFileObj) {
        formData.append("thumbnail", fileList[0].originFileObj);
      }

      await articleAPIWithErrorHandling.updateArticle(id, formData);
      message.success("Cập nhật bài viết thành công!");
      navigate("/articles");
    } catch (error) {
      message.error(error.message || "Không thể cập nhật bài viết");
    } finally {
      setSubmitting(false);
    }
  };

  /*
   * Ảnh bìa PHẢI đi qua onChange, không phải beforeUpload.
   *
   * beforeUpload nhận RcFile (File + uid) — nhét thẳng vào fileList thì phần tử
   * đó KHÔNG có `originFileObj`, nên điều kiện `fileList[0].originFileObj` ở
   * handleSubmit luôn sai và ảnh bìa chưa bao giờ được gửi lên. onChange mới là
   * chỗ antd dựng UploadFile có `originFileObj` (xem BannerTable, vốn chạy đúng).
   */
  const uploadProps = {
    onRemove: () => setFileList([]),
    beforeUpload: () => false, // chặn antd tự upload, ta tự gửi trong FormData
    onChange: ({ fileList: next }) => setFileList(next.slice(-1)),
    fileList,
    maxCount: 1,
    accept: "image/png,image/jpeg,image/jpg,image/gif",
  };
  
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ size: ["small", false, "large", "huge"] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ script: "sub" }, { script: "super" }],
      ["blockquote", "code-block"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      ["link", "image", "video"],
      ["clean"],
    ],
  };

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <Card className="shadow-sm mx-auto">
        <Breadcrumb
          separator=">"
          className="mb-4"
          items={[
            { href: "/", title: <><HomeOutlined /> Trang chủ</> },
            { href: "/articles", title: <><FileTextOutlined /> Tin tức</> },
            { title: "Cập nhật bài viết" },
          ]}
        />
        <Divider />

        <Spin spinning={loading || submitting}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Row gutter={24}>
              <Col xs={24} md={16}>
                <Form.Item
                  name="title"
                  label="Tiêu đề bài viết"
                  rules={[{ required: true, message: "Vui lòng nhập tiêu đề!" }]}
                >
                  <Input placeholder="Nhập tiêu đề tin tức" size="large" />
                </Form.Item>

                <Form.Item
                  name="summary"
                  label="Tóm tắt ngắn"
                >
                  <Input.TextArea rows={3} placeholder="Nhập tóm tắt ngắn..." />
                </Form.Item>

                <Form.Item
                  name="content"
                  label="Nội dung chi tiết"
                  className="mb-12"
                >
                  <ReactQuill 
                    theme="snow" 
                    modules={quillModules}
                    style={{ height: '400px', marginBottom: '50px' }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Card title="Cấu hình & Ảnh bìa" size="small" className="bg-white">
                  <Form.Item
                    name="article_category_id"
                    label="Danh mục"
                    // rules={[{ required: true, message: "Vui lòng chọn danh mục!" }]}
                  >
                    <Select placeholder="Chọn danh mục" allowClear>
                      {categories.map(cat => (
                        <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="publish_date"
                    label="Ngày đăng"
                  >
                    <DatePicker showTime style={{ width: '100%' }} format="DD/MM/YYYY HH:mm" />
                  </Form.Item>

                  <Form.Item
                    name="author"
                    label="Tác giả"
                  >
                    <Input placeholder="Tên tác giả" />
                  </Form.Item>

                  <Form.Item
                    label="Ảnh bìa hiện tại"
                  >
                    {currentThumbnail ? (
                      <div className="mb-4 border rounded p-2 text-center bg-gray-50">
                        <img src={currentThumbnail} alt="Current" className="max-h-40 mx-auto" />
                      </div>
                    ) : (
                      <div className="text-gray-400 mb-2 italic">Chưa có ảnh bìa</div>
                    )}
                    
                    <Upload {...uploadProps} listType="picture">
                      <Button icon={<UploadOutlined />}>Thay đổi ảnh</Button>
                    </Upload>
                  </Form.Item>
                </Card>
              </Col>
            </Row>

            <Divider />

            <Row justify="end">
              <Space>
                <Button onClick={() => navigate("/articles")}>Hủy</Button>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  icon={<SaveOutlined />}
                  loading={submitting}
                >
                  Lưu thay đổi
                </Button>
              </Space>
            </Row>
          </Form>
        </Spin>
      </Card>
    </div>
  );
}
